import { buttonClass } from '@league/ui';
import type { ReactNode, TdHTMLAttributes } from 'react';

/**
 * EVERY LIST OF PLAYERS IS THIS TABLE — owner, 2026-10-16. The roster became a table with headed
 * columns on 2026-10-08, and the waitlist, the league's subs, the players page and the substitute
 * pool stayed lists, so pace ("Steady") and times subbed sat on the row with nothing saying what they
 * were. One component, so a column header is never again something one screen has and another lacks.
 *
 * ON A PHONE THE TABLE GIVES WAY to `MobileList` (owner, 2026-10-16): every screen that shows this
 * table shows the phone list beside it, and each hides at the other's width.
 *
 * `head` is the row of <th>s: the roster swaps its headings for a selection bar while rows are ticked.
 */
export function PlayerTable({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    /* relative: a screen-reader-only heading is positioned, and must scroll with the card rather than widen the page. */
    <div className="relative overflow-x-auto rounded-[14px] border border-light bg-paper max-sm:hidden">
      <table className="w-full min-w-[560px] border-collapse text-left">
        <thead>
          <tr className="h-12 border-b border-light">{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** A column heading. */
export function Th({ children }: { children: ReactNode }) {
  return <th scope="col" className="whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-3">{children}</th>;
}

/** The actions column has no visible heading. */
export function ThActions() {
  return <th scope="col"><span className="sr-only">Actions</span></th>;
}

/** The select-all box in the heading row. */
export function ThSelect({ children }: { children: ReactNode }) {
  return <th scope="col" className="w-12 pl-4">{children}</th>;
}

const CELL = {
  select: 'w-12 pl-4',
  name: 'px-3',
  field: 'px-3',
  actions: 'pr-3',
} as const;

/** A cell; `kind` sets its padding. */
export function Td({ kind = 'field', className = '', children, ...rest }: {
  kind?: keyof typeof CELL;
  children?: ReactNode;
} & TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={`${CELL[kind]} ${className}`} {...rest}>{children}</td>;
}

export const PLAYER_ROW =
  'group h-[60px] border-b border-light transition-colors duration-[120ms] last:border-b-0';
export const PLAYER_NAME =
  'text-[16px] font-semibold text-ink underline-offset-4 group-hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-deep';
/** The quiet text buttons at the end of a row: Invite, Remove and the like — the Button `plain` look. */
export const QUIET_ACTION = `${buttonClass('plain')} disabled:cursor-wait`;
